// @flow

import * as React from 'react';
import {useEffect, useState} from 'react';
import {TextInput} from 'sail/Input';
import classNames from 'classnames';
import Button, {ButtonLink} from 'sail/Button';

import {loadStripe} from '@stripe/stripe-js';
import {Elements, useStripe} from '@stripe/react-stripe-js';

import Box from 'sail/Box';
import Group from 'sail/Group';

import Text from 'sail/Text/Text';

import {DemoContainer} from 'docs/components';
import {useApi} from 'docs/data';
import {prefillReactElement} from 'docs/util';

import {defineMessages, FormattedMessage} from 'react-intl';

const messages = defineMessages({
  nameError: {
    id: 'docsApp.components.cardPaymentDemoOxxo.nameError',
    description: 'Error shown when no name is entered',
    defaultMessage: 'A value is needed in the name field to pay.',
  },
  emailError: {
    id: 'docsApp.components.cardPaymentDemoOxxo.emailError',
    description: 'Error shown when no email is entered',
    defaultMessage: 'A value is needed in the email field to pay.',
  },
  genericError: {
    id: 'docsApp.components.cardPaymentDemoOxxo.genericError',
    description: 'Error shown when an unknown error occurs',
    defaultMessage: "An error occurred. We're looking into it!",
  },
  name: {
    id: 'docsApp.components.cardPaymentDemoOxxo.name',
    description: 'Label for name input',
    defaultMessage: 'Name',
  },
  email: {
    id: 'docsApp.components.cardPaymentDemoOxxo.email',
    description: 'Label for email input',
    defaultMessage: 'Email',
  },
  prefill: {
    id: 'docsApp.components.cardPaymentDemoOxxo.prefill',
    description: 'Link to prefill some input fields',
    defaultMessage: 'Prefill billing details',
  },
  pay: {
    id: 'docsApp.components.cardPaymentDemoOxxo.pay',
    description: 'Submit button label',
    defaultMessage: 'Pay with OXXO',
  },
});

const Demo = () => {
  const stripe = useStripe();
  const {api} = useApi();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');

  const pay = async () => {
    if (!stripe) {
      return;
    }

    if (!name) {
      setError(<FormattedMessage {...messages.nameError} />);
      return;
    }
    if (!email) {
      setError(<FormattedMessage {...messages.emailError} />);
      return;
    }
    setError('');
    setPending(true);

    const response = await api.createPaymentIntent({
      payment_method_type: 'oxxo',
    });

    if (response.clientSecret) {
      const payload = await stripe.confirmOxxoPayment(response.clientSecret, {
        payment_method: {
          billing_details: {
            name,
            email,
          },
        },
      });

      if (payload.error) {
        setError(payload.error);
      }

      setPending(false);
    } else {
      setPending(false);
      const domain = window.location.hostname || 'stripe.com';
      setError(
        domain.match('dev.stripe.me') ? (
          'This demo does not work locally.'
        ) : (
          <FormattedMessage {...messages.genericError} />
        ),
      );
      setTimeout(() => {
        setError('');
      }, 3000);
    }
  };

  const prefillBillingDetails = () => {
    const exampleDetails = [
      {
        name: 'Fulano de Tal',
        email: 'fulano@test.com',
      },
      {
        name: 'Pedro Pérez',
        email: 'pedroperez@test.com',
      },
      {
        name: 'Juan Gómez',
        email: 'juangomez@test.com',
      },
      {
        name: 'María García',
        email: 'mariagarcia@test.com',
      },
      {
        name: 'Andrea Guzmán',
        email: 'andreaguzman@test.com',
      },
    ];
    const details =
      exampleDetails[Math.floor(Math.random() * exampleDetails.length)];
    prefillReactElement(details.name, setName);
    prefillReactElement(details.email, setEmail, 1000);
  };

  return (
    <Group
      spacing={8}
      flexGrow={2}
      flex={{justifyContent: 'center', alignItems: 'center'}}
      className={classNames('CardPaymentDemo-new')}
    >
      <Box>
        <Box padding={{top: 12, bottom: 12}}>
          <Group direction="horizontal" justifyContent="space-between">
            <Group spacing={8}>
              <label htmlFor="oxxo-demo-name">
                <FormattedMessage {...messages.name} />
              </label>
              <TextInput
                id="oxxo-demo-name"
                width={180}
                onChange={(val) => setName(val)}
                placeholder="Juan Gómez"
                value={name}
              />
            </Group>
            <Group spacing={8}>
              <label htmlFor="oxxo-demo-email">
                <FormattedMessage {...messages.email} />
              </label>
              <TextInput
                id="oxxo-demo-email"
                onChange={(val) => setEmail(val)}
                width={180}
                placeholder="juangomez@test.com"
                value={email}
              />
            </Group>
          </Group>
        </Box>
      </Box>
      <Group spacing={12} alignItems="center">
        {error && <Text color="red">{error}</Text>}
        <Button
          color="blue"
          type="submit"
          label={<FormattedMessage {...messages.pay} />}
          className="CardPaymentDemo-pay"
          onClick={pay}
          pending={pending}
          size="medium"
        />
        <ButtonLink
          label={<FormattedMessage {...messages.prefill} />}
          onClick={prefillBillingDetails}
        />
      </Group>
    </Group>
  );
};

export const CardPaymentDemoOxxo = () => {
  const [promise, setPromise] = useState(null);

  // This key needs to match the key in the demo endpoint used to make the request
  const OXXO_DEMO_KEY = 'pk_test_SK1cidVzFunKRxyutFt6tfBd00TMUcRsvV';

  useEffect(() => {
    setPromise(loadStripe(OXXO_DEMO_KEY));
  }, [OXXO_DEMO_KEY]);

  return (
    <Elements stripe={promise}>
      <DemoContainer
        demo={Demo}
        result={() => <></>}
        name="oxxo-payment"
        programmingLanguages={['PHP', 'Node.js', 'Ruby', 'Python']}
      />
    </Elements>
  );
};
